import React, { useRef, useMemo, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { BoardCreate } from '@jira/software__board-create/src/BoardCreate.tsx';
import { useProjectsAnalyticsL2 } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/controllers/use-projects-analytics-l2/index.tsx';
import { CommonNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/common-nav-menu-button/main.tsx';
import { ProjectActionMenu } from '@jira/navigation-apps__project-actions-action-menu/src/ui/ProjectActionMenu.tsx';
import AddIcon from '@atlaskit/icon/core/add';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { CustomTriggerProps } from '@atlaskit/dropdown-menu/types';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import type {
	ProjectActions$data,
	ProjectActions$key,
} from './__generated__/ProjectActions.graphql';
import { isCompanyManagedSoftwareProject } from './utils.tsx';

const createBoard = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-common.project-actions.create-board',

	defaultMessage: 'Create board',
	description: 'Used as the alt text for an icon button that when used navigates to create a board',
});

export type Props = {
	projectFragment: ProjectActions$key;
	isCorePremiumUserSeat?: boolean;
	actionMenuId?: string;
};

const Trigger = ({
	triggerRef,
	isFavourite,
	projectId,
	projectType,
	setCreateBoardButtonRef,
	...props
}: CustomTriggerProps & {
	isFavourite: boolean;
	projectId: string;
	projectType: ProjectActions$data['projectType'];
	setCreateBoardButtonRef: (instance: HTMLButtonElement | null) => void;
}) => {
	const { formatMessage } = useIntl();
	const { sendCreateBoardOnBoardMenuItemAnalyticsEvent } = useProjectsAnalyticsL2();
	// callback to set button element to local ref.
	// to be used as prop for setting focus back to button after modal close.
	const setRef = useCallback(
		(buttonElement: HTMLButtonElement | null) => {
			setCreateBoardButtonRef(buttonElement);
			if (typeof triggerRef === 'function') {
				triggerRef(buttonElement);
			}
		},
		[setCreateBoardButtonRef, triggerRef],
	);
	const onCommonNavMenuButtonClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			const { onClick } = props;
			onClick?.(event);
			sendCreateBoardOnBoardMenuItemAnalyticsEvent({
				starred: isFavourite ?? false,
				recent: !isFavourite,
				projectId,
				projectType,
			});
		},
		[isFavourite, projectId, projectType, props, sendCreateBoardOnBoardMenuItemAnalyticsEvent],
	);

	return (
		<Tooltip content={formatMessage(createBoard)}>
			<CommonNavMenuButton
				{...props}
				onClick={onCommonNavMenuButtonClick}
				icon={(iconProps) => <AddIcon {...iconProps} size="small" />}
				label={formatMessage(createBoard)}
				ref={setRef}
			/>
		</Tooltip>
	);
};
export const ProjectActions = ({
	projectFragment,
	isCorePremiumUserSeat = false,
	actionMenuId,
}: Props): React.JSX.Element => {
	const { sendMeatBallDropDownOnProjectsAnalyticsEvent } = useProjectsAnalyticsL2();
	const createBoardButtonRef = useRef<HTMLButtonElement | null>(null);

	// eslint-disable-next-line @atlassian/relay/use-fragment-naming
	const project = useFragment<ProjectActions$key>(
		graphql`
			fragment ProjectActions on JiraProject {
				name
				projectKey: key @required(action: THROW)
				projectId @required(action: THROW)
				isFavourite
				projectStyle
				projectType @required(action: THROW)
				avatar {
					large
				}
			}
		`,
		projectFragment,
	);

	const onProjectActionMenuClick = useCallback(() => {
		sendMeatBallDropDownOnProjectsAnalyticsEvent({
			starred: project.isFavourite ?? false,
			recent: !project.isFavourite,
			projectId: project.projectId,
			projectType: project.projectType,
		});
	}, [
		project.isFavourite,
		project.projectId,
		project.projectType,
		sendMeatBallDropDownOnProjectsAnalyticsEvent,
	]);

	const projectActionMenu = useMemo(() => {
		return (
			<ProjectActionMenu
				projectName={project.name}
				onClick={onProjectActionMenuClick}
				projectKey={project.projectKey}
				projectId={project.projectId}
				isCorePremiumUserSeat={isCorePremiumUserSeat}
				isProjectMenuReorderingEnabled={project?.isFavourite === true}
				actionMenuId={actionMenuId}
				shouldHandleQueryParamModal
			/>
		);
	}, [
		project.name,
		project.projectKey,
		project.projectId,
		project.isFavourite,
		onProjectActionMenuClick,
		isCorePremiumUserSeat,
		actionMenuId,
	]);

	const setCreateBoardButtonRef = useCallback(
		(instance: HTMLButtonElement | null) => {
			createBoardButtonRef.current = instance;
		},
		[createBoardButtonRef],
	);
	const renderTrigger = useCallback(
		(props: CustomTriggerProps) => (
			<Trigger
				{...props}
				isFavourite={project.isFavourite ?? false}
				projectId={project.projectId}
				projectType={project.projectType}
				setCreateBoardButtonRef={setCreateBoardButtonRef}
			/>
		),
		[project.isFavourite, project.projectId, project.projectType, setCreateBoardButtonRef],
	);
	return (
		<>
			<Flex gap="space.050" alignItems="center">
				{isCompanyManagedSoftwareProject(project.projectStyle, project.projectType) && (
					<BoardCreate
						projectId={project.projectId}
						projectAvatar={project.avatar?.large || undefined}
						triggerRef={createBoardButtonRef}
						renderTrigger={renderTrigger}
					/>
				)}
			</Flex>
			{projectActionMenu}
		</>
	);
};
